import { stripDuplicateTrailingHashtags } from '@mondaygpt/constants'
import { Heart, MessageCircle, Repeat2, Share, BarChart2, Copy, Sparkles } from 'lucide-react'
import { selectCurrentBusiness } from 'modules/business/slices/business-entities.slice'
import { useAppSelector } from 'modules/core'
import { useCallback } from 'react'
import { useBrandKitStore } from '../store/brand-kit-store'
import {
  useActiveEditingSectionStore,
  useEditorFocusStore,
  useSectionContextStore,
} from '../store/conversation-store'
import { type XPostContent } from '../types'
import type { MobileSelectedSection } from '../components/mobile-selection-types'
import { MobileSectionSelectButton } from '../components/MobileSectionSelectButton'
import { GeneratingMediaOverlay } from '../components/AssetMedia'
import { XLogo } from '../components/XLogo'
import { SectionHoverWrapper } from './SectionHoverWrapper'
import { EditableInlineText } from './EditableInlineText'
import { EditableHashtags } from './EditableHashtags'
import { SectionActionBar } from './SectionActionBar'
import { CanvasMediaWithBar, useHasComposerCanvas } from '../composition/editor/ComposerCanvasMedia'
import { useCompositionLoading } from '../composition/editor/use-composition-loading'
import { useIsMobile } from 'hooks/use-mobile'
import { useComposerReadOnly } from '../components/composer-read-only'
import { useCreativeStore } from '../store/creative-store'

// T3: skeleton + chrome use the acid token so the preview chrome adapts to
// dark+light themes. Hex link color (`#1d9bf0`) is intentionally kept — it
// belongs to the user-facing tweet body, not Taka chrome.
const SKEL = 'var(--acid-tint)'
const SKEL_BG =
  'linear-gradient(110deg, var(--acid-tint) 8%, var(--acid-soft) 18%, var(--acid-tint) 33%)'

/** The tweet body: `text`, or the master's `caption` on a sibling-derived X row (what the server publishes). */
function xBodyText(content: XPostContent): string | undefined {
  return content.text || (content as { caption?: string }).caption || undefined
}

function isXPostEmpty(content: XPostContent): boolean {
  return (
    !content.imageUrl &&
    !content.imageAttachmentId &&
    !xBodyText(content) &&
    content.hashtags.length === 0
  )
}

interface XPostPreviewProps {
  content: XPostContent
  generatingFields?: Set<string>
  creativeId?: string
  selectedSectionId?: string | null
  onSectionSelect?: (section: MobileSelectedSection | null) => void
  interactive?: boolean
}

export function XPostPreview({
  content,
  generatingFields,
  creativeId,
  selectedSectionId,
  onSectionSelect,
  interactive = true,
}: XPostPreviewProps) {
  const business = useAppSelector(selectCurrentBusiness)
  const logoUrl = useBrandKitStore(s => s.brandKit.logoId)
  const businessName = business?.name || 'Your Business'
  const setContext = useSectionContextStore(s => s.setContext)
  const requestFocus = useEditorFocusStore(s => s.requestFocus)
  const activeField = useActiveEditingSectionStore(s =>
    creativeId ? s.activeSections[creativeId] : null
  )

  const initials = businessName
    .split(' ')
    .slice(0, 2)
    .map(w => w[0])
    .join('')
    .toUpperCase()

  const hasImage = !!content.imageUrl
  const hasCanvas = useHasComposerCanvas()
  // Suppress the legacy hover while the composition is still loading, so it doesn't flash before
  // the live canvas mounts in its place.
  const compLoading = useCompositionLoading(creativeId)
  const isMobile = useIsMobile()
  const readOnly = useComposerReadOnly()
  const updateCreativeContent = useCreativeStore(s => s.updateCreativeContent)
  const editable = !!creativeId && !readOnly && !isMobile
  const text = stripDuplicateTrailingHashtags(xBodyText(content), content.hashtags)
  const hasText = !!text
  const hasHashtags = content.hashtags.length > 0
  // Strip leading '#' before prefixing — the editor strips it on input but
  // stored creatives or agent-generated tags may include it, which would
  // render as '##foo'.
  const hashtagsText = hasHashtags
    ? content.hashtags.map(h => `#${h.replace(/^#+/, '')}`).join(' ')
    : ''

  const genImage = generatingFields?.has('image') ?? false
  const genText = generatingFields?.has('text') ?? false
  const genHashtags = generatingFields?.has('hashtags') ?? false
  const isGenerating = (generatingFields?.size ?? 0) > 0

  const handleSectionSelect = useCallback(
    (sectionType: string, label: string, preview: string) => {
      if (!onSectionSelect || !creativeId) return
      if (selectedSectionId === sectionType) {
        onSectionSelect(null)
      } else {
        onSectionSelect({
          sectionId: sectionType,
          sectionType,
          creativeType: 'x_post',
          label,
          preview,
        })
      }
    },
    [creativeId, selectedSectionId, onSectionSelect]
  )

  const handleAskAI = useCallback(
    (field: string, label: string, preview: string) => {
      if (creativeId) {
        setContext(creativeId, { sectionId: field, sectionType: field, label, preview })
      }
    },
    [creativeId, setContext]
  )

  const handleEdit = useCallback(
    (field: string) => {
      if (creativeId) {
        requestFocus(creativeId, field)
      }
    },
    [creativeId, requestFocus]
  )

  if (isXPostEmpty(content)) {
    return (
      <div className="w-full max-w-lg rounded-2xl border border-gray-200 bg-white p-6">
        <div className="flex flex-col items-center justify-center gap-3 py-12 text-gray-400">
          <XLogo size={36} className="text-gray-400" />
          <p className="text-sm">Your X post will appear here</p>
        </div>
      </div>
    )
  }

  return (
    <div
      className="relative w-full max-w-lg overflow-hidden rounded-2xl bg-white transition-all duration-300"
      style={{
        border: '1px solid #e5e7eb',
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
      }}
    >
      {isGenerating && <GeneratingMediaOverlay shape="post" tone="light" radius={16} />}
      {/* Header */}
      <div className="flex items-center gap-3 px-4 pt-3 pb-1">
        {logoUrl ? (
          <img
            src={logoUrl}
            alt={businessName}
            className="h-10 w-10 flex-shrink-0 rounded-full object-cover"
          />
        ) : (
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-900 text-xs font-bold text-white">
            {initials}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1">
            <p className="truncate text-sm font-bold text-gray-900">{businessName}</p>
            <span className="text-xs text-gray-400">
              @{businessName.toLowerCase().replace(/\s+/g, '')}
            </span>
          </div>
        </div>
      </div>

      {/* Text — inline-editable on desktop; mobile keeps its tap→sheet flow. */}
      {editable && !genText ? (
        <SectionActionBar
          actions={[
            {
              icon: <Copy size={12} />,
              label: 'Copy',
              confirmLabel: 'Copied',
              onClick: () =>
                navigator.clipboard
                  .writeText(content.text ?? '')
                  .catch(err => console.error('copy text failed:', err)),
            },
            {
              icon: <Sparkles size={12} />,
              label: 'Ask Taka',
              onClick: () => handleAskAI('text', 'Text', (content.text ?? '').slice(0, 80)),
            },
          ]}
        >
          <div className="px-4 py-2">
            <EditableInlineText
              initialValue={text}
              onCommit={t => updateCreativeContent(creativeId!, { text: t })}
              ariaLabel="Edit tweet text"
              placeholder="What's happening?"
              className="block text-[15px] leading-relaxed whitespace-pre-line text-gray-900 outline-none focus:outline-none"
            />
          </div>
        </SectionActionBar>
      ) : hasText ? (
        <div
          className={`relative cursor-pointer transition-all duration-200 md:cursor-default ${selectedSectionId === 'text' ? 'ring-2 ring-acid/30 ring-inset' : ''}`}
          onClick={() =>
            onSectionSelect && handleSectionSelect('text', 'Text', content.text.slice(0, 80))
          }
        >
          <div className="relative overflow-hidden px-4 py-2">
            {activeField === 'text' && (
              <div className="pointer-events-none absolute inset-0 z-[5] bg-acid/[0.08] transition-opacity duration-300" />
            )}
            <p className="text-[15px] leading-relaxed whitespace-pre-line text-gray-900">{text}</p>
          </div>
          {!genText && onSectionSelect && interactive && (
            <MobileSectionSelectButton
              selected={selectedSectionId === 'text'}
              onSelect={() => handleSectionSelect('text', 'Text', content.text.slice(0, 80))}
            />
          )}
        </div>
      ) : genText ? (
        <div className="relative overflow-hidden px-4 py-2">
          <div className="flex flex-col gap-2.5">
            <div className="h-2.5 w-full rounded-md" style={{ background: SKEL }} />
            <div className="h-2.5 w-3/4 rounded-md" style={{ background: SKEL }} />
          </div>
        </div>
      ) : null}

      {/* Hashtags — editable chips on desktop; mobile keeps its tap→sheet flow. */}
      {editable && !genHashtags ? (
        <SectionActionBar
          actions={[
            {
              icon: <Copy size={12} />,
              label: 'Copy',
              confirmLabel: 'Copied',
              onClick: () =>
                navigator.clipboard
                  .writeText(hashtagsText)
                  .catch(err => console.error('copy hashtags failed:', err)),
            },
            {
              icon: <Sparkles size={12} />,
              label: 'Ask Taka',
              onClick: () => handleAskAI('hashtags', 'Hashtags', hashtagsText),
            },
          ]}
        >
          <div className="px-4 pb-2">
            <EditableHashtags
              value={content.hashtags ?? []}
              onCommit={h => updateCreativeContent(creativeId!, { hashtags: h })}
              className="text-[15px] text-[#1d9bf0]"
            />
          </div>
        </SectionActionBar>
      ) : hasHashtags ? (
        <div
          className={`relative cursor-pointer transition-all duration-200 md:cursor-default ${selectedSectionId === 'hashtags' ? 'ring-2 ring-acid/30 ring-inset' : ''}`}
          onClick={() =>
            onSectionSelect && handleSectionSelect('hashtags', 'Hashtags', hashtagsText)
          }
        >
          <div className="relative overflow-hidden px-4 pb-2">
            {activeField === 'hashtags' && (
              <div className="pointer-events-none absolute inset-0 z-[5] bg-acid/[0.08] transition-opacity duration-300" />
            )}
            <p className="text-[15px] text-[#1d9bf0]">{hashtagsText}</p>
          </div>
          {!genHashtags && onSectionSelect && interactive && (
            <MobileSectionSelectButton
              selected={selectedSectionId === 'hashtags'}
              onSelect={() => handleSectionSelect('hashtags', 'Hashtags', hashtagsText)}
            />
          )}
        </div>
      ) : genHashtags ? (
        <div className="relative overflow-hidden px-4 pb-2">
          <div className="h-2.5 w-24 rounded-md" style={{ background: SKEL }} />
        </div>
      ) : null}

      {/* Image */}
      {hasCanvas && hasImage ? (
        <CanvasMediaWithBar
          clipClassName="mx-4 mb-3 w-[calc(100%-2rem)] overflow-hidden rounded-2xl"
          interactive={interactive}
          onTap={() => handleSectionSelect('image', 'Image', 'Post media')}
        />
      ) : (
        <div
          className={`relative mx-4 mb-3 w-[calc(100%-2rem)] cursor-pointer overflow-hidden rounded-2xl transition-all duration-200 md:cursor-default ${selectedSectionId === 'image' ? 'ring-2 ring-acid/30 ring-inset' : ''}`}
          onClick={() => onSectionSelect && handleSectionSelect('image', 'Image', 'Post media')}
        >
          {hasImage ? (
            <>
              {!genImage && creativeId && !compLoading ? (
                <SectionHoverWrapper
                  fieldId="image"
                  onAskAI={() => handleAskAI('image', 'Image', 'Post image')}
                  onEdit={() => handleEdit('image')}
                  isActiveEditing={activeField === 'image'}
                >
                  <img
                    src={content.imageUrl ?? undefined}
                    alt="Post image"
                    className="w-full object-cover"
                  />
                </SectionHoverWrapper>
              ) : (
                <img
                  src={content.imageUrl ?? undefined}
                  alt="Post image"
                  className="w-full object-cover"
                />
              )}
              {!genImage && onSectionSelect && interactive && (
                <MobileSectionSelectButton
                  selected={selectedSectionId === 'image'}
                  onSelect={() => handleSectionSelect('image', 'Image', 'Post image')}
                />
              )}
            </>
          ) : genImage ? (
            <div
              className="flex aspect-video w-full items-center justify-center"
              style={{ background: SKEL_BG }}
            >
              <div
                className="z-[1] flex h-16 w-16 items-center justify-center rounded-2xl"
                style={{ background: SKEL }}
              >
                <span className="text-2xl">🎨</span>
              </div>
            </div>
          ) : null}
        </div>
      )}

      {/* Engagement bar */}
      <div className="flex items-center justify-between border-t px-4 py-2">
        <button className="flex items-center gap-1 rounded-full p-1.5 text-gray-400 hover:bg-[#1d9bf0]/10 hover:text-[#1d9bf0]">
          <MessageCircle size={16} />
        </button>
        <button className="flex items-center gap-1 rounded-full p-1.5 text-gray-400 hover:bg-green-500/10 hover:text-green-500">
          <Repeat2 size={16} />
        </button>
        <button className="flex items-center gap-1 rounded-full p-1.5 text-gray-400 hover:bg-pink-500/10 hover:text-pink-500">
          <Heart size={16} />
        </button>
        <button className="flex items-center gap-1 rounded-full p-1.5 text-gray-400 hover:bg-[#1d9bf0]/10 hover:text-[#1d9bf0]">
          <BarChart2 size={16} />
        </button>
        <button className="flex items-center gap-1 rounded-full p-1.5 text-gray-400 hover:bg-[#1d9bf0]/10 hover:text-[#1d9bf0]">
          <Share size={16} />
        </button>
      </div>
    </div>
  )
}
